import { ReactNode, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import appModalTokens, {
  ModalSize,
  ModalTone,
  SHEET_MEDIA,
} from "./AppModal.tokens";
import {
  ModalAlertIcon,
  ModalCheckIcon,
  ModalCloseIcon,
  ModalDocIcon,
  ModalLockIcon,
  ModalTrashIcon,
} from "./AppModalIcons";
import {
  bodySx,
  closeButtonSx,
  destructiveButtonSx,
  footerActionsSx,
  footerSx,
  grabberSx,
  grabberWrapSx,
  headerSx,
  headerTextSx,
  iconTileSx,
  paperSx,
  rootSx,
  subtitleSx,
  titleSx,
} from "./AppModal.styles";

const t = appModalTokens;

/**
 * One entry of MUI's array `sx` form. `SxProps` itself includes the array case,
 * so it cannot be used as the element type — an `SxProps[]` would allow nested
 * arrays that MUI does not accept.
 */
type SxEntry = Exclude<SxProps<Theme>, readonly any[]>;

/**
 * Normalises an optional `sx` into the array form MUI composes, so a caller's
 * override can be layered on top of the shell's own styles without spreading
 * (which silently drops array/callback `sx` values).
 */
const toSxArray = (sx?: SxProps<Theme>): SxEntry[] => {
  if (!sx) return [];
  return (Array.isArray(sx) ? sx : [sx]) as SxEntry[];
};

/**
 * Per-instance id source for `aria-labelledby` / `aria-describedby`.
 *
 * React 18 ships `useId`, but the `@types/react` this project is pinned to does
 * not declare it, so the ids come from a module counter latched into state on
 * first render instead. The app is client-rendered, so there is no hydration
 * mismatch to worry about.
 */
let modalInstanceCount = 0;
const useModalId = () =>
  useState(() => `app-modal-${(modalInstanceCount += 1)}`)[0];

/** Glyph a tone falls back to when the caller does not supply one. */
const TONE_ICON: Record<ModalTone, ReactNode> = {
  default: <ModalDocIcon />,
  destructive: <ModalTrashIcon />,
  success: <ModalCheckIcon />,
  neutral: <ModalAlertIcon />,
  amber: <ModalLockIcon />,
};

export interface AppModalProps {
  open: boolean;
  /**
   * Dismiss handler. Fires for the close button, Esc and a scrim click —
   * whichever of those the props below leave enabled. Omit it and the modal
   * becomes non-dismissable (no close button, no Esc, no scrim click).
   */
  onClose?: () => void;

  /* ── Header ────────────────────────────────────────────────────────────── */

  /** One line. Doubles as the dialog's accessible name. */
  title?: ReactNode;
  /** Optional supporting line under the title. */
  subtitle?: ReactNode;
  /** Glyph inside the tinted tile. Defaults to the tone's own icon. */
  icon?: ReactNode;
  /** Drop the icon tile; the title then starts at the left padding edge. */
  hideIcon?: boolean;
  /** Suppress the header entirely — for bodies that draw their own chrome. */
  hideHeader?: boolean;
  /** Hide only the close affordance (Esc and the scrim are unaffected). */
  hideClose?: boolean;
  /** Extra control seated immediately left of the close button. */
  headerAction?: ReactNode;
  /**
   * Full-bleed strip above the header. The signer surface uses it for its brand
   * accent bar; nothing else in the app needs it, but it lives here rather than
   * in a second modal implementation.
   */
  topSlot?: ReactNode;

  /* ── Shape ─────────────────────────────────────────────────────────────── */

  /** Tone drives the icon tile tint and the primary button fill only. */
  tone?: ModalTone;
  /** Width step: `sm` 420 · `md` 520 (default) · `lg` 720 · `xl` 960. */
  size?: ModalSize;
  /** Explicit max width in px when none of the steps fit. */
  width?: number;
  /** Keep the centred card on phones instead of dropping to a bottom sheet. */
  disableSheet?: boolean;
  /** Go edge-to-edge on phones instead of a sheet — for viewers and editors. */
  fullScreenOnMobile?: boolean;

  /* ── Body ──────────────────────────────────────────────────────────────── */

  children?: ReactNode;
  /** Remove the body's inset so the content can run to the shell edge. */
  disableBodyPadding?: boolean;
  /**
   * Cap the body at 60vh and scroll it, leaving the header and footer fixed.
   * Defaults to `true`; turn it off for bodies that manage their own scroller.
   */
  scrollBody?: boolean;
  bodySx?: SxProps<Theme>;
  /**
   * Overrides for the header row — the icon, title and close button.
   *
   * For a shell whose body supplies its own inset: the row carries its own
   * padding, which would otherwise stack on top of that and leave the close
   * button further in than everything below it.
   */
  headerSx?: SxProps<Theme>;

  /* ── Footer ────────────────────────────────────────────────────────────── */

  /** Verb-first label, e.g. "Send request". Omit to drop the primary button. */
  primaryLabel?: ReactNode;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  /** Spinner in the button; the shell itself never swaps out. */
  primaryLoading?: boolean;
  /** Submits the wrapping form rather than firing `onPrimary`. */
  primaryType?: "button" | "submit";
  cancelLabel?: ReactNode;
  /** Defaults to `onClose`. */
  onCancel?: () => void;
  hideCancel?: boolean;
  /** Replaces the generated buttons wholesale. */
  footer?: ReactNode;
  /** Low-emphasis action pinned to the far left of the footer bar. */
  footerStart?: ReactNode;
  /** Drop the footer bar — bodies whose actions live inside the content. */
  hideFooter?: boolean;

  /* ── Behaviour ─────────────────────────────────────────────────────────── */

  /** Scrim clicks stop dismissing. Always on for the destructive tone. */
  disableBackdropClose?: boolean;
  /** Esc stops dismissing. Use sparingly — the system expects Esc to work. */
  disableEscapeClose?: boolean;
  /**
   * A submit is in flight: Esc, the scrim and the close button are all locked
   * until it settles.
   */
  busy?: boolean;
  /** Turns the shell into a `<form>`; the primary button then submits it. */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Accessible name when there is no visible `title`. */
  ariaLabel?: string;
  /** Escape hatch for one-off shell tweaks. Prefer a size step first. */
  paperSx?: SxProps<Theme>;
  /**
   * Class on the paper — the element that carries `role="dialog"`.
   *
   * For code that has to recognise its own modal in the DOM. The tour gates
   * itself on `[role="dialog"]:not(.inkfree-tour-modal)`, so without this its
   * own welcome card would read as a blocking dialog and it would never run.
   */
  paperClassName?: string;
  /**
   * Stacking order of the whole overlay. Only for a modal that must clear
   * another fixed layer — the tour's spotlight sits at 1250.
   */
  zIndex?: number;
  /** Rendered outside the shell — nested pickers, portals, snackbars. */
  slot?: ReactNode;
  /**
   * Pointer handlers applied to the overlay root. Needed only when the modal
   * opens over a draggable surface and its events would otherwise reach it —
   * the signature pad over a self-sign field is the one case in the app.
   */
  rootPointerHandlers?: Pick<
    React.DOMAttributes<HTMLDivElement>,
    "onMouseDown" | "onTouchEnd" | "onPointerDown"
  >;
}

/**
 * The single modal shell for the whole app.
 *
 * Built on MUI's `Dialog`, so the focus trap, scroll lock, Esc handling, portal
 * and `aria-modal` semantics all come for free and nothing that already worked
 * has to be re-implemented. What this adds is the design system's chrome: the
 * scrim, the five-slot layout, the tone treatment, the size steps and the
 * bottom-sheet behaviour below 768px.
 *
 * Slots, top to bottom: icon tile + title + subtitle + close, body, footer.
 * Optional slots collapse and nothing else moves.
 */
const AppModal = (props: AppModalProps) => {
  const {
    open,
    onClose,
    title,
    subtitle,
    icon,
    hideIcon = false,
    hideHeader = false,
    hideClose = false,
    headerAction,
    topSlot,
    tone = "default",
    size = "md",
    width,
    disableSheet = false,
    fullScreenOnMobile = false,
    children,
    disableBodyPadding = false,
    scrollBody = true,
    primaryLabel,
    onPrimary,
    primaryDisabled = false,
    primaryLoading = false,
    primaryType = "button",
    cancelLabel,
    onCancel,
    hideCancel = false,
    footer,
    footerStart,
    hideFooter = false,
    disableBackdropClose = false,
    disableEscapeClose = false,
    busy = false,
    onSubmit,
    ariaLabel,
    slot,
    rootPointerHandlers,
    paperClassName,
    zIndex,
  } = props;

  const isNarrow = useMediaQuery(SHEET_MEDIA);
  const isFullScreen = isNarrow && fullScreenOnMobile;
  const isSheet = isNarrow && !disableSheet && !fullScreenOnMobile;

  const instanceId = useModalId();
  const titleId = `${instanceId}-title`;
  const bodyId = `${instanceId}-body`;

  const handleClose = (
    _event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    // "Loading in place": once a submit is in flight nothing dismisses the
    // shell until it settles.
    if (busy) return;
    if (
      reason === "backdropClick" &&
      (disableBackdropClose || tone === "destructive")
    ) {
      return;
    }
    if (reason === "escapeKeyDown" && disableEscapeClose) return;
    onClose?.();
  };

  const showHeader = !hideHeader && (Boolean(title) || Boolean(subtitle));
  // Kept mounted while a submit is in flight — only disabled — so the header
  // never reflows mid-request. The shell must not swap out.
  const showClose = Boolean(onClose) && !hideClose;
  const glyph = icon ?? TONE_ICON[tone];

  /* The generated footer only appears when the caller asked for at least one
     button; a bare `footer` slot replaces it entirely. */
  const hasCancelButton = Boolean(onClose || onCancel) && !hideCancel;
  const generatedFooter =
    Boolean(primaryLabel) || (hasCancelButton && Boolean(cancelLabel));
  const showFooter = !hideFooter && (Boolean(footer) || generatedFooter);

  const renderHeader = () => {
    if (!showHeader) {
      // The close button still has to be reachable on a header-less shell.
      if (!showClose) return null;
      return (
        <Box sx={[headerSx(isSheet), ...toSxArray(props.headerSx)]}>
          <Box sx={headerTextSx} />
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            disabled={busy}
            sx={closeButtonSx}
          >
            <ModalCloseIcon size={t.header.closeGlyphSize} />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box sx={[headerSx(isSheet), ...toSxArray(props.headerSx)]}>
        {!hideIcon && <Box sx={iconTileSx(tone)}>{glyph}</Box>}
        <Box sx={headerTextSx}>
          {Boolean(title) && (
            <Typography id={titleId} component="h2" sx={titleSx(isSheet)}>
              {title}
            </Typography>
          )}
          {Boolean(subtitle) && (
            <Typography sx={subtitleSx(isSheet)}>{subtitle}</Typography>
          )}
        </Box>
        {headerAction}
        {showClose && (
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            disabled={busy}
            sx={closeButtonSx}
          >
            <ModalCloseIcon size={t.header.closeGlyphSize} />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderActions = () => (
    <>
      {hasCancelButton && Boolean(cancelLabel) && (
        <Button
          variant="outlined"
          onClick={onCancel ?? onClose}
          disabled={busy}
        >
          {cancelLabel}
        </Button>
      )}
      {Boolean(primaryLabel) && (
        <Button
          variant="contained"
          type={primaryType}
          onClick={primaryType === "submit" ? undefined : onPrimary}
          disabled={primaryDisabled || primaryLoading}
          /*
            Only `destructive` restyles the fill. `amber` used to mint a
            darkened gold, but a solid gold button is an orange fill by another
            name — the house rule puts those on the brand colour and keeps amber
            for the accents (icon tile, badge, surface). The gold survives where
            it is *text* on a light tint, which is what it was darkened for.
          */
          sx={tone === "destructive" ? destructiveButtonSx : undefined}
          startIcon={
            primaryLoading ? (
              <CircularProgress size={15} thickness={5} color="inherit" />
            ) : undefined
          }
        >
          {primaryLabel}
        </Button>
      )}
    </>
  );

  const renderFooter = () => {
    if (!showFooter) return null;
    const hasStart = Boolean(footerStart);
    return (
      <Box sx={footerSx({ isSheet, hasStart })}>
        {hasStart && footerStart}
        {hasStart ? (
          <Box sx={footerActionsSx(isSheet)}>
            {footer ?? renderActions()}
          </Box>
        ) : (
          footer ?? renderActions()
        )}
      </Box>
    );
  };

  const shell = (
    <>
      {topSlot}
      {isSheet && (
        <Box sx={grabberWrapSx}>
          <Box sx={grabberSx} />
        </Box>
      )}
      {renderHeader()}
      <DialogContent
        id={bodyId}
        // Composed as an array rather than spread: `SxProps` may legally be an
        // array or a callback, and spreading either of those into an object
        // both loses the styles and fails to type-check.
        sx={[
          bodySx({
            isSheet,
            scrollable: scrollBody,
            disablePadding: disableBodyPadding,
            hasHeader: showHeader || showClose,
          }),
          ...toSxArray(props.bodySx),
        ]}
      >
        {children}
      </DialogContent>
      {renderFooter()}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isFullScreen}
      transitionDuration={t.motion.transitionDuration}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
      aria-describedby={children ? bodyId : undefined}
      sx={[rootSx(isSheet), ...(zIndex === undefined ? [] : [{ zIndex }])]}
      {...rootPointerHandlers}
      // Turning the paper itself into the `<form>` keeps the flex column intact
      // and lets the primary button submit with `type="submit"`, so Enter in a
      // field behaves the way it does outside a modal.
      PaperProps={{
        ...(onSubmit
          ? { component: "form", noValidate: true, onSubmit }
          : null),
        ...(paperClassName ? { className: paperClassName } : null),
        sx: [
          paperSx({ size, width, isSheet, fullScreen: isFullScreen }),
          ...toSxArray(props.paperSx),
        ],
      }}
    >
      {shell}
      {slot}
    </Dialog>
  );
};

export default AppModal;
